import { useEffect } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import type { MappingPagedCollectionOfEventSearchResultAndEventGridItem } from "src/http/types";
import { Content } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import {
  CarouselId,
  CarouselItemType,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { GridCardBase } from "src/website-apps/shared/components/grid-card-base";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { CAROUSEL_SIZES, VENUE_CAROUSEL_IDS } from "src/website-apps/shared/components/responsive-carousel/constants";
import { ResponsiveCarousel } from "src/website-apps/shared/components/responsive-carousel/ResponsiveCarousel";

const titleStyles = css({
  marginBlockEnd: "primitives.100",
  textStyle: "title.medium",
  tablet: {
    textStyle: "header.medium",
  },
});

interface Props {
  venueName: string | null;
  events: MappingPagedCollectionOfEventSearchResultAndEventGridItem | undefined;
  isLoading: boolean;
  onHasContent?: () => void;
}

export const VenueTrendingCarousel = ({ venueName, events, isLoading, onHasContent }: Props) => {
  const storePageEvent = useStorePageEvent();
  const isPhone = !useMediaQuery(Breakpoint.Tablet);

  const hasEvents = Boolean(events?.items?.length);

  useEffect(() => {
    if (hasEvents) {
      onHasContent?.();
    }
  }, [hasEvents, onHasContent]);

  if (!hasEvents && !isLoading) {
    return null;
  }

  const title = venueName ? (
    <h2 className={titleStyles}>
      {/* "Trending events" */}
      <Content id="cid$#3577100#" />
    </h2>
  ) : null;

  return (
    <>
      {title}
      <ResponsiveCarousel
        analyticsCarouselId={CarouselId.TrendingEvents}
        analyticsItemType={CarouselItemType.Event}
        carouselId={VENUE_CAROUSEL_IDS.TRENDING_EVENTS_V2}
        gapPx={16}
        hasHoverEffect={!isPhone}
        hidePaginationButtons={isPhone}
        imageHeightHint={226}
        isLoading={isLoading}
        items={events?.items ?? []}
        loadingCount={isPhone ? 2 : 4}
        size={isPhone ? CAROUSEL_SIZES.marqueeMobile : CAROUSEL_SIZES.marqueeDesktop}
        useFlex
        renderItem={(item, index) => (
          <GridCardBase
            entityId={item.eventId}
            entityType={ENTITY_TYPES.EVENT}
            event={item}
            eventDate={item.eventDateTimeFormatted?.shortDate ?? undefined}
            eventTime={item.eventDateTimeFormatted?.time ?? undefined}
            imageUrl={item.imageUrl ?? ""}
            onClick={() =>
              storePageEvent(PE_TYPE.CLICK, PE_KEYS.VENUE_TRENDING_CAROUSEL_CLICK, `${item?.eventId ?? ""}-${index}`)
            }
            size={isPhone ? "mobile" : "default"}
            title={item.eventName ?? ""}
            url={item.url ?? ""}
          />
        )}
      />
    </>
  );
};
